import React, { useEffect, useState } from "react";
import styles from "../../assets/containers/book.module.css";
import { getBookProgress, getLessons } from "../../service/service";
import BlockCard from "./blockCard";
import LessonCard from "../lessons/lessonCard";
import Banner from "./banner";
import { useAppGlobalConfig } from "../../providers/AppGlobalConfigContext";
import { getAllUserIds } from "../../helpers/user";

const EMPTY_BOOK_PROGRESS = {
  isLoaded: false,
  book: null,
  batchesById: {},
  lessonsById: {},
};

const Book = (props) => {
  const {
    currentBook,
    courses,
    changeBook,
    currentLanguage,
    hiddenBanner = false,
  } = props;
  const { isEmbedMode } = useAppGlobalConfig();
  const [groupedLessons, setGroupedLessons] = useState([]);
  const [totalLessonsCount, setTotalLessonsCount] = useState(0);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [bookProgress, setBookProgress] = useState(EMPTY_BOOK_PROGRESS);

  const getBatchData = (batchLesson, totalLessons, validGroupIndex) => {
    return {
      id: batchLesson.id,
      // Exibe o nome do batch, caso exista. Caso contrário, exibe "Batch: X"
      name:
        batchLesson.name && batchLesson.name.trim() !== ""
          ? batchLesson.name
          : `Batch: ${validGroupIndex}`,
      totalLessons: totalLessons,
      // Verifica se a propriedade 'batch' existe e se contém pelo menos um item antes de acessar o título e imagem
      title:
        batchLesson.batch &&
        batchLesson.batch.length > 0 &&
        batchLesson.batch[0].title
          ? batchLesson.batch[0].title
          : null,
      image:
        batchLesson.batch &&
        batchLesson.batch.length > 0 &&
        batchLesson.batch[0].image
          ? batchLesson.batch[0].image
          : null,
    };
  };

  const fetchAllLessons = async (bookId, page = 1, accumulatedLessons = []) => {
    try {
      const response = await getLessons(bookId, {
        page_size: 100,
        page,
      });

      const allLessons = [...accumulatedLessons, ...response.data];

      if (
        response &&
        response.links &&
        response.links.next &&
        response.links.next !== ""
      ) {
        return fetchAllLessons(bookId, page + 1, allLessons);
      }

      return allLessons;
    } catch (error) {
      console.error("Erro ao buscar todas as lições:", error);
      return accumulatedLessons;
    }
  };

  const loadLessons = async () => {
    try {
      setLoadingLessons(true);

      const lessonData = await fetchAllLessons(currentBook.id);

      if (!lessonData || !lessonData.length) {
        console.error(`Nenhuma lição encontrada para este livro`);
        setGroupedLessons([]);
        setTotalLessonsCount(0);
        return;
      }
      // Map to store grouped lessons
      const tempGroupedLessons = new Map();

      // Iterate over lessons to create groups
      lessonData.forEach((lesson) => {
        const parentId = lesson.parent !== null ? lesson.parent : lesson.id;
        if (!tempGroupedLessons.has(parentId)) {
          tempGroupedLessons.set(parentId, []);
        }
        tempGroupedLessons.get(parentId).push(lesson);
      });

      // Convert the map into an array keeping the original order
      const groupedLessonsArray = [];
      let validGroupIndex = 1; // Índice para manter a ordem dos batches que não têm título definido

      lessonData.forEach((lesson) => {
        const parentId = lesson.parent !== null ? lesson.parent : lesson.id;
        if (tempGroupedLessons.has(parentId)) {
          const group = tempGroupedLessons.get(parentId);
          if (group.length > 1) {
            const batchLesson = lessonData.find((l) => l.id === parentId);
            const batch = getBatchData(
              batchLesson,
              group.length,
              validGroupIndex,
            );

            groupedLessonsArray.push({
              batch,
              group,
              index: validGroupIndex,
            });
            validGroupIndex++;
          } else {
            groupedLessonsArray.push({ group, index: null });
          }
          tempGroupedLessons.delete(parentId);
        }
      });

      setGroupedLessons(groupedLessonsArray);
      setTotalLessonsCount(lessonData.length);
    } catch (err) {
      console.log("Erro ao carregar lições:", err);
    } finally {
      setLoadingLessons(false);
    }
  };

  useEffect(() => {
    loadLessons();
  }, [currentBook]);

  useEffect(() => {
    if (!currentBook || !currentBook.id) return;
    if (!groupedLessons.length) return;

    let cancelled = false;

    const fetchConsolidatedProgress = async () => {
      const tenant = isEmbedMode ? "cupheadless" : "cupfranchise";
      const lessonIds = [];
      groupedLessons.forEach((groupObj) =>
        (groupObj.group || []).forEach((lesson) => {
          if (lesson && lesson.id) lessonIds.push(lesson.id);
        }),
      );

      let userIds = [];
      try {
        userIds = await getAllUserIds();
      } catch (_e) {
        userIds = [];
      }
      const [primary, ...fallbackIds] = userIds;
      if (!primary) {
        if (!cancelled)
          setBookProgress({ ...EMPTY_BOOK_PROGRESS, isLoaded: true });
        return;
      }

      try {
        const res = await getBookProgress(tenant, currentBook.id, {
          userId: primary,
          userIds: fallbackIds.join(","),
          lessonIds: lessonIds.join(","),
          totalLessons: totalLessonsCount,
        });
        if (cancelled) return;

        const payload = (res && res.data) || {};
        const batchesById = {};
        (payload.batches || []).forEach((batch) => {
          if (batch && batch.batchId !== undefined) {
            batchesById[batch.batchId] = batch;
          }
        });
        const lessonsById = {};
        (payload.lessons || []).forEach((lesson) => {
          if (lesson && lesson.lessonId !== undefined) {
            lessonsById[lesson.lessonId] = lesson;
          }
        });
        setBookProgress({
          isLoaded: true,
          book: payload.book || null,
          batchesById,
          lessonsById,
        });
      } catch (err) {
        console.warn(
          "Consolidated book progress unavailable:",
          err && err.message,
        );
        if (!cancelled)
          setBookProgress({ ...EMPTY_BOOK_PROGRESS, isLoaded: true });
      }
    };

    setBookProgress(EMPTY_BOOK_PROGRESS);
    fetchConsolidatedProgress();

    return () => {
      cancelled = true;
    };
  }, [currentBook, groupedLessons, totalLessonsCount, isEmbedMode]);

  // This is to prevent the information from the last accessed batch to be used
  // when a lesson without batch is completed.
  useEffect(() => {
    localStorage.removeItem("batch");
  }, []);

  return (
    <>
      {!hiddenBanner && (
        <Banner
          currentBook={currentBook}
          courses={courses}
          totalLessonsCount={totalLessonsCount}
          changeBook={changeBook}
          currentLanguage={currentLanguage}
          lessonsAvailable={groupedLessons.length}
          bookProgress={bookProgress}
        />
      )}

      {loadingLessons && (
        <div className="loading-lessons-container">
          <div className="spinner-border home-loading" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div style={{ color: "rgb(117,117,117)" }}>Loading lessons...</div>
        </div>
      )}

      <div className={styles["book-container"]}>
        {groupedLessons.map((groupObj, groupIndex) =>
          groupObj.group.length === 1 ? (
            <LessonCard
              key={groupIndex}
              lesson={groupObj.group[0]}
              groupIndex={groupIndex}
              bookProgress={bookProgress}
            />
          ) : (
            <BlockCard
              key={groupIndex}
              lessons={groupObj.group}
              validIndex={groupObj.index}
              groupIndex={groupIndex}
              batch={groupObj.batch}
              bookProgress={bookProgress}
            />
          ),
        )}
      </div>
    </>
  );
};

export default Book;
